import { OktaAuth } from '@okta/okta-auth-js';
import axios from 'axios';
import jwt_decode from 'jwt-decode';
import { BehaviorSubject } from 'rxjs';
import {
  distinctUntilChanged,
  filter,
  map,
  shareReplay,
  skip,
} from 'rxjs/operators';
import { UserId } from 'api/models';
import { API_HOST, API_NAMESPACE, API_VERSION } from 'config/environment';
import { InitWorkerApi } from 'initWorker';
import { ActiveTab } from './active-tab';
import { logger } from './logger';

let okta: OktaAuth | null = null;

export const setOkta = (auth: OktaAuth): void => {
  okta = auth;
};

export const getOkta = (): OktaAuth | null => {
  return okta;
};

const getDecodedValue = (token: string): { emailId: string } | null => {
  try {
    return jwt_decode<{ emailId: string }>(token);
  } catch (e) {
    return null;
  }
};

const key = '__api_key';

export const API_KEY = key;

export interface ApiKeyCreateResponse {
  apiKeyId: number;
  apiKey: string;
  createdBy: {
    userId: number;
  };
}

type KeyId = number;
type ApiKeyValue = string;
type CreatedBy = number;
type ApiKey = ['api_key', ApiKeyValue, CreatedBy, KeyId];

const sanitize = (value: string | null): ApiKey | null =>
  JSON.parse(value ?? 'null');

const get = (): ApiKey | null => sanitize(localStorage.getItem(key));
const _apiKey$ = new BehaviorSubject<ApiKey | null>(get());

_apiKey$
  .pipe(
    skip(1),
    filter((k) => k == null),
  )
  .subscribe(() => {
    ActiveTab.save();
    /**
     * A redirect or window.location.reload() is recommended to ensure proper
     * re-initialization of app and okta auth sdk. Refer:
     * https://www.npmjs.com/package/@okta/okta-auth-js#closesession
     */
    window.location.reload();
  });

window.addEventListener('storage', (event) => {
  if (event.storageArea !== localStorage || event.key !== key) {
    return;
  }
  _apiKey$.next(sanitize(event.newValue));
});
const exists = (): boolean => get() != null;
const creator = ([, , author]: ApiKey): UserId => UserId(author);
const clear = async () => {
  const api_key = get();

  if (api_key == null) {
    return;
  }
  localStorage.removeItem(key);
  try {
    await axios.delete('users/delete-api-key', {
      baseURL: `${API_HOST}/${API_NAMESPACE}/${API_VERSION}`,
      headers: { 'api-key': api_key[1] },
    });
    logger.debug('deleted api key');
  } catch (error) {
    logger.error('could not delete api key', { error });
  }
};
const value = ([, apiKeyValue]: ApiKey): string => apiKeyValue;

export enum TokenType {
  Okta = 1,
  Rocketlane,
  External,
}
const create = async (
  token: string,
  tokenType: TokenType,
  externalRefKey?: string,
  projectId?: string,
): Promise<ApiKey> => {
  logger.debug('fetching api key for access token');

  let params;
  const tokenPayload = getDecodedValue(token);

  if (tokenType === TokenType.Rocketlane) {
    params = { token };
  } else if (tokenType === TokenType.External) {
    params = {
      'rl-token': token,
    };
  }

  const response = await axios.post<ApiKeyCreateResponse>(
    tokenType === TokenType.External
      ? '/users/invite-create-api-key'
      : '/users/create-api-key',
    TokenType.External
      ? {
          externalRefKey: externalRefKey ?? undefined,
          projectId: projectId ?? undefined,
          user: {
            emailId: tokenPayload?.emailId,
          },
        }
      : null,
    {
      baseURL: `${API_HOST}/${API_NAMESPACE}/${API_VERSION}`,
      headers:
        tokenType === TokenType.Okta
          ? { Authorization: `Bearer ${token}` }
          : undefined,
      params,
    },
  );
  const api_key: ApiKey = [
    'api_key',
    response.data.apiKey,
    response.data?.createdBy?.userId,
    response.data.apiKeyId,
  ];

  logger.debug('fetched api key for access token');
  localStorage.setItem(key, JSON.stringify(api_key));
  InitWorkerApi.setData();
  logger.debug('set api key');

  logger.debug('clearing okta tokens');
  okta?.tokenManager.clear();
  logger.debug('cleared okta tokens');

  return api_key;
};
const tokens = () =>
  _apiKey$.pipe(
    map((k) => (k == null ? null : k[1])),
    distinctUntilChanged(),
    shareReplay(1),
  );
const header = (): Record<string, string> => {
  const result = get();

  return result == null ? {} : { 'api-key': value(result) };
};

export const ApiKey = {
  get,
  exists,
  creator,
  create,
  clear,
  tokens,
  header,
};
